import React, { useState, useRef, useEffect } from 'react';
import { Lock, Eye, EyeOff, AlertCircle, Fingerprint, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { unlockVault, verifyPIN, setVaultKey } from '../utils/vaultCrypto';
import { isBiometricBackupEnabled, recoverWithBiometric, setupBiometricBackup, isBiometricAvailable } from '../utils/biometricBackup';

const VaultUnlock = ({ salt, testValue, onUnlock, userName }) => {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [showSetupBiometric, setShowSetupBiometric] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [unlockedKey, setUnlockedKey] = useState(null);
  const [unlockedPin, setUnlockedPin] = useState(null);
  const inputRefs = useRef([]);

  // Reference for hidden input
  const hiddenInputRef = useRef(null);
  const pinString = pin.join('');
  
  const handleHiddenInput = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    const newPin = [...pin];
    for (let i = 0; i < 6; i++) {
      newPin[i] = value[i] || '';
    }
    setPin(newPin);
    setError('');
    
    // Auto-submit when all 6 digits entered
    if (value.length === 6) {
      setTimeout(() => handleUnlock(value), 100);
    }
  };
  
  const handleHiddenKeyDown = (e) => {
    if (e.key === 'Backspace' && pinString.length > 0) {
      const newPin = [...pin];
      newPin[pinString.length - 1] = '';
      setPin(newPin);
    }
    if (e.key === 'Enter') {
      handleUnlock(pin.join(''));
    }
  };
  
  const focusPinInput = () => {
    hiddenInputRef.current?.focus();
  };

  useEffect(() => {
    // Check if biometric backup is enabled
    const enabled = isBiometricBackupEnabled();
    setBiometricEnabled(enabled);
    
    // Check if biometric is available (for showing setup option)
    if (!enabled) {
      isBiometricAvailable().then(available => {
        // Also show on mobile devices even if check fails
        setBiometricAvailable(available || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
      });
    }
    
    // Auto-focus the input
    const timer = setTimeout(() => {
      focusPinInput();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value.slice(-1);
    setPin(newPin);
    setError('');
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newPin.every(d => d !== '')) {
      setTimeout(() => handleUnlock(newPin.join('')), 100);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!pin[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
    if (e.key === 'Enter') {
      handleUnlock(pin.join(''));
    }
  };

  const handleUnlock = async (pinString = pin.join('')) => {
    if (pinString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Verify PIN by attempting to decrypt test value
      const isValid = await verifyPIN(pinString, salt, testValue);
      
      if (!isValid) {
        setAttempts(prev => prev + 1);
        setError('Incorrect PIN. Please try again.');
        setPin(['', '', '', '', '', '']);
        setTimeout(() => focusPinInput(), 100);
        setLoading(false);
        return;
      }
      
      // PIN is correct
      const key = await unlockVault(pinString, salt);
      setVaultKey(key);
      
      // If biometric not enabled and available, offer to set it up
      if (!biometricEnabled && biometricAvailable) {
        setUnlockedKey(key);
        setUnlockedPin(pinString);
        setShowSetupBiometric(true);
        setLoading(false);
      } else {
        onUnlock(key);
      }
    } catch (err) {
      setAttempts(prev => prev + 1);
      setError('Incorrect PIN. Please try again.');
      setPin(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      console.error('Unlock error:', err);
      setLoading(false);
    }
  };

  const handleSetupBiometric = async () => {
    setLoading(true);
    setError('');
    try {
      const userId = localStorage.getItem('sehatSetu_user') 
        ? JSON.parse(localStorage.getItem('sehatSetu_user')).id 
        : `user_${Date.now()}`;
      
      const result = await setupBiometricBackup(userId, salt, unlockedPin);
      
      if (result.success) {
        setBiometricEnabled(true);
        // Show success briefly then unlock
        setTimeout(() => {
          onUnlock(unlockedKey);
        }, 1000);
      } else {
        setError(result.error || 'Face ID setup failed. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Biometric setup error:', err);
      setError('Face ID setup failed. You can try again later in Settings.');
      setLoading(false);
    }
  };

  const skipBiometricSetup = () => {
    onUnlock(unlockedKey);
  };

  // Show biometric setup prompt after successful PIN entry
  if (showSetupBiometric) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0D2340] to-[#0B7EA3] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              {biometricEnabled ? (
                <CheckCircle className="h-8 w-8 text-green-600" />
              ) : (
                <Fingerprint className="h-8 w-8 text-[#0B7EA3]" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {biometricEnabled ? 'Face ID Enabled!' : 'Enable Face ID?'}
            </CardTitle>
            <CardDescription>
              {biometricEnabled 
                ? 'You can now use Face ID to unlock your vault.'
                : 'Never type your PIN again - unlock with Face ID'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}
            
            {!biometricEnabled && (
              <>
                <Button 
                  onClick={handleSetupBiometric}
                  className="w-full h-12 bg-[#0D2340] hover:bg-[#0D2340]/90"
                  disabled={loading}
                  data-testid="setup-biometric-btn"
                >
                  <Fingerprint className="h-5 w-5 mr-2" />
                  {loading ? 'Setting up Face ID...' : 'Enable Face ID'}
                </Button>
                
                <Button 
                  variant="ghost"
                  onClick={skipBiometricSetup}
                  className="w-full h-12 text-slate-600"
                  disabled={loading}
                >
                  Skip for now
                </Button>
              </>
            )}
            
            {biometricEnabled && (
              <Button 
                onClick={() => onUnlock(unlockedKey)}
                className="w-full h-12 bg-[#0B7EA3] hover:bg-[#0B7EA3]/90"
              >
                Continue to Dashboard
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0D2340] to-[#0B7EA3] flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-[#0B7EA3]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-[#0B7EA3]" />
          </div>
          <CardTitle className="text-2xl">Unlock Your Health Vault</CardTitle>
          <CardDescription>
            Welcome back{userName ? `, ${userName}` : ''}! Enter your 6-digit PIN
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="relative">
            {/* Hidden input for actual typing */}
            <input
              ref={hiddenInputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              value={pinString}
              onChange={handleHiddenInput}
              onKeyDown={handleHiddenKeyDown}
              className="absolute opacity-0 w-full h-full cursor-pointer"
              maxLength={6}
              disabled={loading}
              data-testid="unlock-pin-hidden-input"
            />
            {/* Visual display boxes */}
            <div 
              className="flex justify-center gap-3 cursor-pointer"
              onClick={focusPinInput}
              onTouchStart={focusPinInput}
            >
              {pin.map((digit, index) => (
                <div
                  key={index}
                  className={`w-12 h-14 flex items-center justify-center text-2xl font-bold border-2 rounded-xl 
                             transition-all select-none
                             ${index === pinString.length ? 'border-[#0B7EA3] ring-2 ring-[#0B7EA3]/20' : 'border-slate-200'}
                             ${digit ? 'bg-slate-50' : 'bg-white'}
                             ${loading ? 'opacity-50' : ''}`}
                  data-testid={`unlock-pin-display-${index}`}
                >
                  {digit ? (showPin ? digit : '•') : ''}
                </div>
              ))}
            </div>
          </div>
          
          <button
            onClick={() => setShowPin(!showPin)}
            className="flex items-center justify-center gap-2 text-sm text-slate-600 mx-auto"
          >
            {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPin ? 'Hide PIN' : 'Show PIN'}
          </button>
          
          {error && (
            <div className="flex items-center justify-center gap-2 text-red-500 text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
          
          {attempts >= 3 && !biometricEnabled && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              <p className="font-medium">Forgot your PIN?</p>
              <p className="mt-1">
                Unfortunately, we cannot recover your data without the PIN. 
                This is by design to keep your health data private.
              </p>
            </div>
          )}
          
          <Button 
            onClick={() => handleUnlock()}
            className="w-full h-12 text-lg bg-[#0D2340] hover:bg-[#0D2340]/90"
            disabled={pin.join('').length !== 6 || loading}
            data-testid="unlock-btn"
          >
            {loading ? 'Unlocking...' : 'Unlock'}
          </Button>
          
          {/* Biometric unlock option */}
          {biometricEnabled && (
            <div className="pt-2 border-t border-slate-200">
              <button
                onClick={async () => {
                  setLoading(true);
                  setError('');
                  try {
                    let result = await recoverWithBiometric();
                    
                    if (result.success && result.data) {
                      const key = await unlockVault(result.data.pin, salt);
                      setVaultKey(key);
                      onUnlock(key);
                    } else {
                      const errorMsg = result.error || 'Biometric recovery failed';
                      if (errorMsg.includes('cancelled') || errorMsg.includes('denied')) {
                        setError('Face ID was cancelled. Please try again or use your PIN.');
                      } else {
                        setError('Face ID failed. Please use your PIN.');
                      }
                    }
                  } catch (err) {
                    console.error('Biometric unlock error:', err);
                    setError('Face ID failed. Please use your PIN.');
                  } finally {
                    setLoading(false);
                  }
                }}
                className="w-full flex items-center justify-center gap-2 py-3 text-[#0B7EA3] hover:bg-[#0B7EA3]/5 rounded-lg transition-colors"
                disabled={loading}
                data-testid="biometric-unlock-btn"
              >
                <Fingerprint className="h-5 w-5" />
                {loading ? 'Authenticating...' : 'Use Face ID / Touch ID'}
              </button>
              <p className="text-xs text-center text-slate-400 mt-1">
                Tap to scan your face or fingerprint
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VaultUnlock;
